import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { ShipBuilderState, ShipStats } from "../../types/ship";
import { OptionalSystem } from "../../types/optionalSystems";
import { OPTIONAL_SYSTEM_TYPES } from "../../constants/optionalSystems";
import { OccupancyType } from "../../types/stateroom";

// Base selector
const selectShipBuilderState = (state: RootState): ShipBuilderState =>
  state.shipBuilder;

// Weapon selectors
const selectWeapons = createSelector(
  selectShipBuilderState,
  (state) => state.weapons || {},
);

const selectWeaponCounts = createSelector(selectShipBuilderState, (state) => {
  const weapons = state.weapons || {};
  const counts = {
    turret: 0,
    bay: 0,
    screen: 0,
    missile: 0,
    torpedo: 0,
    mine: 0,
    other: 0,
  };

  Object.values(weapons).forEach((weapon) => {
    if (weapon && typeof weapon === "object" && "type" in weapon) {
      const type = weapon.type as string;
      if (type in counts) {
        counts[type as keyof typeof counts]++;
      } else {
        counts.other++;
      }
    }
  });

  return counts;
});

// Stateroom selectors
const selectStaterooms = createSelector(
  selectShipBuilderState,
  (state) => state.staterooms || [],
);

const selectStateroomCounts = createSelector(
  selectShipBuilderState,
  (state) => {
    const staterooms = state.staterooms || [];
    const occupancyCounts: Record<OccupancyType, number> = {
      single: 0,
      double: 0,
    };

    staterooms.forEach((stateroom) => {
      if (stateroom && typeof stateroom === "object" && "type" in stateroom) {
        const type = stateroom.type as OccupancyType;
        if (type in occupancyCounts) {
          occupancyCounts[type]++;
        }
      }
    });

    return occupancyCounts;
  },
);

const selectLowBerthCounts = createSelector(selectStaterooms, (staterooms) => {
  if (staterooms.length === 0) {
    return {
      lowBerths: 0,
      emergencyLowBerths: 0,
    };
  }

  const stateroom = staterooms[0];
  return {
    lowBerths: stateroom.lowBerths?.count || 0,
    emergencyLowBerths: stateroom.emergencyLowBerths?.count || 0,
  };
});

// Drive selectors
const selectDrives = createSelector(
  selectShipBuilderState,
  (state) => state.drives || [],
);

const selectHasJumpDrive = createSelector(selectDrives, (drives) =>
  drives.some((drive) => drive.type === "jump" && drive.rating > 0),
);

const selectHasManeuverDrive = createSelector(selectDrives, (drives) =>
  drives.some((drive) => drive.type === "maneuver" && drive.rating > 0),
);

// Optional systems selectors
const selectOptionalSystems = createSelector(
  selectShipBuilderState,
  (state) => state.optionalSystems || [],
);

const selectAvailableOptionalSystems = createSelector(
  [selectShipBuilderState],
  (state): OptionalSystem[] => {
    const techLevel = state.techLevel;
    return OPTIONAL_SYSTEM_TYPES.filter((system: any) => {
      const systemTechLevel = system.techLevel || 7;
      return systemTechLevel <= techLevel;
    }) as OptionalSystem[];
  },
);

const selectSmallCraftCount = createSelector(
  selectOptionalSystems,
  (systems) => systems.filter((system) => system.type === "smallCraft").length,
);

// Crew selectors
const selectCrew = createSelector(
  selectShipBuilderState,
  (state) => state.crew,
);

const selectTotalCrew = createSelector(selectCrew, (crew): number => {
  if (!crew) return 0;
  const departments = {
    captain: 1,
    pilot: crew.pilots?.length || 0,
    engineer: crew.engineers?.length || 0,
    gunner: crew.gunners?.length || 0,
    steward: crew.stewards?.length || 0,
  };
  return Object.values(departments).reduce(
    (total: number, count: number) => total + count,
    0,
  );
});

// Stats selectors
const selectShipStats = createSelector(
  selectShipBuilderState,
  (state): ShipStats => state.shipStats,
);

const selectCargoSpace = createSelector(
  selectShipStats,
  (stats) => stats.cargoSpace,
);

const selectTotalWeight = createSelector(
  selectShipStats,
  (stats) => stats.totalWeight,
);

const selectTotalCost = createSelector(
  selectShipStats,
  (stats) => stats.totalCost,
);

export const selectors = {
  // Basic ship selectors
  selectShipSize: createSelector(
    selectShipBuilderState,
    (state) => state.shipSize,
  ),

  selectTechLevel: createSelector(
    selectShipBuilderState,
    (state) => state.techLevel,
  ),

  selectHull: createSelector(selectShipBuilderState, (state) => state.hull),

  selectArmor: createSelector(selectShipBuilderState, (state) => state.armor),

  selectBridge: createSelector(selectShipBuilderState, (state) => state.bridge),

  selectPowerPlant: createSelector(
    selectShipBuilderState,
    (state) => state.powerPlant,
  ),

  selectDrives,
  selectWeapons,
  selectOptionalSystems,
  selectAvailableOptionalSystems,
  selectCrew,
  selectTotalCrew,
  selectStaterooms,
  selectCargo: createSelector(selectShipBuilderState, (state) => state.cargo),

  selectFinalDetails: createSelector(
    selectShipBuilderState,
    (state) => state.finalDetails,
  ),

  selectShipStats,
  selectCargoSpace,
  selectTotalWeight,
  selectTotalCost,

  // Derived selectors
  selectHasJumpDrive,
  selectHasManeuverDrive,
  selectSmallCraftCount,
  selectWeaponCounts,
  selectStateroomCounts,
  selectLowBerthCounts,
} as const;
